import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useRequestQueryContext } from 'src/features/requests/use-request-query-context';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useCompanyQueryContextData } from 'src/features/companies/use-company-query-context';
import { useProfile } from 'src/models';
import { useRequestStatusMap } from 'src/pages/RequestPage/RequestStatusChangeForm/RequestStatusChangeForm.utils';
import { useOpenRequestStatusChangeModal } from 'src/features/requests/request-status-change/use-open-request-status-change-modal';
import { Button } from 'src/components/ui/button';
import { If } from 'src/components/If';
import { Link } from 'src/components/ui/link';
import { appRoutes } from 'src/routes';
import { RequestAttentionAction } from 'src/pages/RequestPage/RequestActionComponents/RequestAttentionAction';
import { authQueries } from 'src/api/queries';
import { ChevronLeft, FileVideo2, FolderInput, Megaphone, Video } from 'lucide-react';
import { getFilenameFromUrl, type DownloadableFile } from 'src/lib/utils/download-utils';
import { DownloadFilesDropdown } from 'src/components/download-files-dropdown';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { PageHeading, PageHeadingTitle } from 'src/components/ui/page-heading';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'src/components/ui/breadcrumb';
import { RequestIdBadge } from 'src/components/request-id-badge/request-id-badge';
import { defaultSortFilter } from 'src/api/services/RequestClient';
import { requestsResponseHelper } from 'src/features/requests/requests-response.helper';
import { DefaultToMultiOutcomeRequestTransformButton } from 'src/features/requests/request-table/default-to-multi-outcome-request-transform-button';
import { Loader2 } from 'src/components/ui/animated-loader2';

const RequestDefaultPageContentTop = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const client = useQueryClient();

  const requestQuery = useRequestQueryContext();
  const request = useDecoratedRequestContext();
  const company = useCompanyQueryContextData();

  const user = useProfile({ enabled: false, retry: false });

  const { canChangeStatus } = useRequestStatusMap();

  const { openRequestStatusChangeModal } = useOpenRequestStatusChangeModal();

  // Collect downloadable files from request
  const getDownloadableFiles = useCallback((): DownloadableFile[] => {
    const files: DownloadableFile[] = [];

    // Add media files (use restoration_download_url as fallback for archived files)
    request.media?.forEach((media) => {
      const downloadUrl = media.index_url || media.restoration_download_url;
      if (downloadUrl) {
        files.push({
          url: downloadUrl,
          filename: media.file_name || getFilenameFromUrl(downloadUrl),
          mimeType: media.mime_type || undefined
        });
      }
    });

    // Add asset URLs (if they are direct file links). Prefer the asset's
    // original client filename (#2465) — for gallery imports the backend
    // coalesces this to the source media's name — and only derive from the
    // hashed storage URL when no name is available.
    request.assets?.forEach((asset, index) => {
      if (asset.url && (asset.url.includes('.') || asset.url.includes('download'))) {
        files.push({
          url: asset.url,
          filename: asset.file_name || getFilenameFromUrl(asset.url, `asset-${index + 1}`),
          mimeType: asset.mime_type || undefined,
        });
      }
    });

    return files;
  }, [request.media, request.assets]);

  const changeStatusButtonProps: React.ComponentProps<typeof Button> = {
    onClick: () =>
      openRequestStatusChangeModal({ requestId: request.id, companyId: company?.id ?? '' }),
    className: 'tw-order-first md:tw-order-last tw-grow sm:tw-grow-0',
    size: 'sm',
  };

  if (user.isAdmin && !request.hasEditor) {
    changeStatusButtonProps['title'] = t(
      "Status is not editable on requests that doesn't have assigned editor.",
    )!;
    changeStatusButtonProps['disabled'] = true;
  }

  if (!canChangeStatus(request?.status)) {
    changeStatusButtonProps['title'] = t("Status can't be changed for completed requests.")!;
    changeStatusButtonProps['disabled'] = true;
  }

  return (
    <div ref={ref} className={className} {...props}>
      <If
        when={!!children}
        else={
          <div className={'tw-mb-4 tw-flex tw-flex-wrap tw-gap-4 md:tw-mb-10'}>
            <div
              className={
                'tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4 md:tw-justify-start'
              }
            >
              <PageHeading>
                <Breadcrumb>
                  <BreadcrumbList className={'tw-flex-nowrap'}>
                    <BreadcrumbItem>
                      <Button
                        variant={'unset'}
                        size={'iconXs'}
                        onClick={() => {
                          if (location.state && location.state.from) {
                            navigate(location.state.from);
                          } else {
                            navigate({
                              pathname: generatePath(appRoutes.companyRequestsList, {
                                companyId: company?.id ?? '',
                              }),
                              search: defaultSortFilter,
                            });
                          }
                        }}
                      >
                        <ChevronLeft className={'tw-size-4'} />
                      </Button>
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link to={appRoutes.companiesList}>{t('Companies')}</Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link
                          to={generatePath(appRoutes.company, { companyId: company?.id ?? '' })}
                        >
                          {company?.name ?? t('unset')!}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbLink asChild>
                        <Link
                          to={{
                            pathname: generatePath(appRoutes.companyRequestsList, {
                              companyId: company?.id ?? '',
                            }),
                            search: defaultSortFilter,
                          }}
                          state={location.state}
                        >
                          {t('Requests')}
                        </Link>
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbPage>
                      {t('common:revisions', {
                        defaultValue: 'Revisions',
                      })}
                    </BreadcrumbPage>
                  </BreadcrumbList>
                </Breadcrumb>
                <PageHeadingTitle className={'tw-flex-nowrap'}>
                  <div>
                    <Video className={'tw-size-6'} />
                  </div>
                  <span className={'tw-line-clamp-1'}>
                    {request?.title ?? t('unset', { ns: 'common' })!}
                  </span>
                  <RequestIdBadge id={request.id} />
                </PageHeadingTitle>
              </PageHeading>
            </div>
            <div
              className={'tw-ms-auto tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2'}
            >
              <If when={request.canRequestAttention}>
                <RequestAttentionAction
                  form={{
                    requestId: request.id,
                    initialValues: {
                      attention_text: request?.attention_text || '',
                    },
                    onSubmit: async ({ attention_text }) => {
                      requestQuery.setQueryData({
                        attention_text,
                        requested_attention_at: new Date().toDateString(),
                      });
                      client.invalidateQueries({
                        queryKey: [authQueries.requests, request?.status],
                      });
                    },
                  }}
                  as={Button}
                  variant={'outline'}
                  size={'iconStartSm'}
                  className={'tw-grow sm:tw-grow-0'}
                >
                  <Megaphone className={'tw-size-5'} />
                  {t('Request attention')}
                </RequestAttentionAction>
              </If>

              <If when={requestsResponseHelper.canBeTransformedToMultiOutcome(request)}>
                <DefaultToMultiOutcomeRequestTransformButton
                  size={'iconStartSm'}
                  variant={'outline'}
                  className={'tw-grow sm:tw-grow-0'}
                  request={request}
                >
                  {(props) => (
                    <>
                      {props.isLoading ? (
                        <Loader2 size={16} variant={'unset'} />
                      ) : (
                        <FolderInput size={16} />
                      )}
                      {t('Transform')}
                    </>
                  )}
                </DefaultToMultiOutcomeRequestTransformButton>
              </If>

              {request.hasMedia && (
                <DownloadFilesDropdown
                  getFiles={getDownloadableFiles}
                  zipFileName={`${request.title || 'project'}-files`}
                  className="tw-grow sm:tw-grow-0"
                />
              )}

              <If when={request.hasRevisions}>
                <Button
                  asChild
                  variant={'outline'}
                  size={'iconStartSm'}
                  className={'tw-grow sm:tw-grow-0'}
                >
                  <Link
                    variant={'unset'}
                    to={generatePath(appRoutes.requestRevisions, {
                      companyId: company?.id ?? '',
                      requestId: request.id,
                    })}
                  >
                    <FileVideo2 className={'tw-size-5'} />
                    {t('Revisions')}
                  </Link>
                </Button>
              </If>

              <Button {...changeStatusButtonProps}>{t('Change status')}</Button>
            </div>
          </div>
        }
      >
        {children}
      </If>
    </div>
  );
});
RequestDefaultPageContentTop.displayName = 'RequestDefaultPageContentTop';

export { RequestDefaultPageContentTop };
